import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { Alert,Box,Button,Chip,CircularProgress,IconButton,Paper,Snackbar,Stack,Tab,Tabs,TextField,Typography } from "@mui/material";
import { useCallback,useEffect,useState } from "react";
import { useAuth } from "../../context/AuthContext";
import AttendanceModals from "./AdminAttendance/AttendanceModals";
import Sidebar from "./UserPanel/Sidebar";

const WEEKDAYS={1:"Lunes",2:"Martes",3:"Miércoles",4:"Jueves",5:"Viernes",6:"Sábado",7:"Domingo"};

function getToday(){
  const date=new Date();
  const offset=date.getTimezoneOffset();
  const localDate=new Date(date.getTime()-offset*60*1000);
  return localDate.toISOString().split("T")[0];
}

function getApiMessage(data,fallback){
  if(typeof data?.message==="string")return data.message;
  if(typeof data?.detail==="string")return data.detail;
  if(data?.errors&&typeof data.errors==="object")return Object.values(data.errors).flat().join(" ");
  return fallback;
}

function formatDate(value){
  if(!value)return "Sin límite";
  const date=new Date(`${value}T00:00:00`);
  if(Number.isNaN(date.getTime()))return value;
  return new Intl.DateTimeFormat("es-EC",{year:"numeric",month:"2-digit",day:"2-digit"}).format(date);
}

function formatTime(value){
  if(!value)return "--:--";
  if(String(value).includes("T")){
    const date=new Date(value);
    if(!Number.isNaN(date.getTime()))return new Intl.DateTimeFormat("es-EC",{hour:"2-digit",minute:"2-digit"}).format(date);
  }
  return String(value).slice(0,5);
}

function formatMinutes(value){
  const minutes=Number(value||0);
  const hours=Math.floor(minutes/60);
  const remaining=minutes%60;
  if(!hours)return `${remaining} min`;
  if(!remaining)return `${hours} h`;
  return `${hours} h ${remaining} min`;
}

function statusLabel(value){
  return {PRESENT:"Presente",INCOMPLETE:"Incompleta",ABSENT:"Ausente",JUSTIFIED:"Justificada"}[value]||value||"Sin estado";
}

function entryLabel(value){
  return {EARLY:"Anticipada",ON_TIME:"A tiempo",LATE:"Con retraso",NO_ENTRY:"Sin entrada",NO_SCHEDULE:"Sin horario"}[value]||value||"Sin entrada";
}

function exitLabel(value){
  return {EARLY:"Salida anticipada",ON_TIME:"Salida normal",OVERTIME:"Con horas extra",NO_EXIT:"Sin salida",NO_SCHEDULE:"Sin horario"}[value]||value||"Sin salida";
}

export default function AdminAttendance(){
  const { fetchUser }=useAuth();
  const [organization,setOrganization]=useState(null);
  const [mobileOpen,setMobileOpen]=useState(false);
  const [tab,setTab]=useState(0);
  const [loading,setLoading]=useState(true);
  const [schedules,setSchedules]=useState([]);
  const [people,setPeople]=useState([]);
  const [assignments,setAssignments]=useState([]);
  const [records,setRecords]=useState([]);
  const [recordDate,setRecordDate]=useState(()=>getToday());
  const [scheduleModal,setScheduleModal]=useState({open:false,schedule:null});
  const [assignmentModal,setAssignmentModal]=useState({open:false,assignment:null});
  const [deleting,setDeleting]=useState(null);
  const [feedback,setFeedback]=useState({open:false,severity:"success",message:""});

  const showFeedback=useCallback((severity,message)=>{
    setFeedback({open:true,severity,message});
  },[]);

  const readJson=async(response)=>{
    try{return await response.json();}catch{return {};}
  };

  const loadData=useCallback(async()=>{
    setLoading(true);
    try{
      const [organizationResponse,schedulesResponse,peopleResponse,assignmentsResponse,recordsResponse]=await Promise.all([
        fetchUser("/tenants/api/logo/",{method:"GET",cache:"no-store"}),
        fetchUser("/attendance/api/schedules/",{method:"GET",cache:"no-store"}),
        fetchUser("/attendance/api/people/",{method:"GET",cache:"no-store"}),
        fetchUser("/attendance/api/assignments/",{method:"GET",cache:"no-store"}),
        fetchUser("/attendance/api/records/",{method:"GET",cache:"no-store"})
      ]);
      const organizationData=await readJson(organizationResponse);
      const schedulesData=await readJson(schedulesResponse);
      const peopleData=await readJson(peopleResponse);
      const assignmentsData=await readJson(assignmentsResponse);
      const recordsData=await readJson(recordsResponse);
      if(!organizationResponse.ok||organizationData.ok!==true||!organizationData.tenant)throw new Error(getApiMessage(organizationData,"No se pudo cargar la organización."));
      if(!schedulesResponse.ok||schedulesData.ok!==true)throw new Error(getApiMessage(schedulesData,"No se pudieron cargar los horarios."));
      if(!peopleResponse.ok||peopleData.ok!==true)throw new Error(getApiMessage(peopleData,"No se pudieron cargar los usuarios."));
      if(!assignmentsResponse.ok||assignmentsData.ok!==true)throw new Error(getApiMessage(assignmentsData,"No se pudieron cargar las asignaciones."));
      if(!recordsResponse.ok||recordsData.ok!==true)throw new Error(getApiMessage(recordsData,"No se pudieron cargar las asistencias."));
      setOrganization(organizationData.tenant);
      setSchedules(Array.isArray(schedulesData.schedules)?schedulesData.schedules:[]);
      setPeople(Array.isArray(peopleData.people)?peopleData.people:[]);
      setAssignments(Array.isArray(assignmentsData.assignments)?assignmentsData.assignments:[]);
      setRecords(Array.isArray(recordsData.records)?recordsData.records:[]);
    }catch(error){
      showFeedback("error",error instanceof Error?error.message:"No se pudo conectar con el servidor.");
    }finally{
      setLoading(false);
    }
  },[fetchUser,showFeedback]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const deleteSchedule=async(schedule)=>{
    if(!window.confirm(`¿Deseas eliminar el horario ${schedule.name}?`))return;
    setDeleting(`schedule-${schedule.id}`);
    try{
      const response=await fetchUser(`/attendance/api/schedules/${schedule.id}/`,{method:"DELETE"});
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,"No se pudo eliminar el horario."));
      showFeedback("success",data.message||"Horario eliminado correctamente.");
      await loadData();
    }catch(error){
      showFeedback("error",error instanceof Error?error.message:"No se pudo eliminar el horario.");
    }finally{
      setDeleting(null);
    }
  };

  const deleteAssignment=async(assignment)=>{
    if(!window.confirm(`¿Deseas quitar el horario asignado a ${assignment.person?.name}?`))return;
    setDeleting(`assignment-${assignment.id}`);
    try{
      const response=await fetchUser(`/attendance/api/assignments/${assignment.id}/`,{method:"DELETE"});
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,"No se pudo eliminar la asignación."));
      showFeedback("success",data.message||"Asignación eliminada correctamente.");
      await loadData();
    }catch(error){
      showFeedback("error",error instanceof Error?error.message:"No se pudo eliminar la asignación.");
    }finally{
      setDeleting(null);
    }
  };

  const filteredRecords=records.filter((record)=>{
    if(!record?.attendance_date)return false;
    return String(record.attendance_date).substring(0,10)===recordDate;
  });

  if(loading&&!organization){
    return <Box sx={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",backgroundColor:"#f5f5f5"}}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{minHeight:"100vh",display:"flex",backgroundColor:"#f5f5f5"}}>
      <Sidebar mobileOpen={mobileOpen} onClose={()=>setMobileOpen(false)} organization={organization} />
      <Box component="main" sx={{flexGrow:1,minWidth:0,padding:{xs:2,sm:3,md:4}}}>
        <Box sx={{width:"100%",maxWidth:1250,margin:"0 auto"}}>
          <Box sx={{display:"flex",alignItems:{xs:"flex-start",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:2,marginBottom:3}}>
            <Box sx={{display:"flex",alignItems:"center",gap:1}}>
              <IconButton onClick={()=>setMobileOpen(true)} sx={{display:{xs:"inline-flex",md:"none"}}}><MenuRoundedIcon /></IconButton>
              <Typography component="h1" sx={{color:"#111111",fontSize:{xs:26,md:32},fontWeight:800,lineHeight:1.2}}>Asistencias</Typography>
            </Box>
            {tab===0&&<Button variant="contained" onClick={()=>setScheduleModal({open:true,schedule:null})} sx={{width:{xs:"100%",sm:"auto"},minHeight:44,borderBottom:"3px solid #b00020"}}>Nuevo horario</Button>}
            {tab===1&&<Button variant="contained" onClick={()=>setAssignmentModal({open:true,assignment:null})} sx={{width:{xs:"100%",sm:"auto"},minHeight:44,borderBottom:"3px solid #b00020"}}>Asignar horario</Button>}
          </Box>
          <Paper elevation={0} sx={{width:"100%",border:"1px solid #e1e1e4",borderRadius:3,overflow:"hidden"}}>
            <Tabs value={tab} onChange={(_,value)=>setTab(value)} variant="scrollable" scrollButtons="auto" sx={{paddingX:2,borderBottom:"1px solid #e5e7eb","& .MuiTab-root":{minHeight:58,fontWeight:800,textTransform:"none"},"& .Mui-selected":{color:"#b00020"},"& .MuiTabs-indicator":{height:3,backgroundColor:"#b00020"}}}>
              <Tab label="Horarios" />
              <Tab label="Asignaciones" />
              <Tab label="Registros" />
            </Tabs>
            {tab===0&&(
              <Box sx={{padding:{xs:2,sm:2.5}}}>
                {loading?<Box sx={{minHeight:240,display:"flex",alignItems:"center",justifyContent:"center"}}><CircularProgress /></Box>:schedules.length===0?<EmptyState title="No hay horarios de asistencia" text="Crea un horario para definir entrada, salida y lunch." action="Crear horario" onClick={()=>setScheduleModal({open:true,schedule:null})} />:(
                  <Stack spacing={1.5}>
                    {schedules.map((schedule)=>(
                      <Box key={schedule.id} sx={{padding:{xs:2,sm:2.5},border:"1px solid #e5e7eb",borderRadius:2.5,backgroundColor:"#ffffff"}}>
                        <Box sx={{display:"flex",alignItems:{xs:"flex-start",md:"center"},justifyContent:"space-between",flexDirection:{xs:"column",md:"row"},gap:2}}>
                          <Box sx={{minWidth:0}}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography sx={{fontSize:19,fontWeight:800}}>{schedule.name}</Typography>
                              <Chip size="small" label={schedule.is_active?"Activo":"Inactivo"} sx={{fontWeight:700,color:schedule.is_active?"#047857":"#b00020",backgroundColor:schedule.is_active?"#ecfdf5":"#fff1f2"}} />
                            </Stack>
                            <Typography sx={{color:"#6b7280",fontSize:13,fontWeight:600,marginTop:0.6}}>Vigencia: {formatDate(schedule.valid_from)} hasta {formatDate(schedule.valid_until)}</Typography>
                          </Box>
                          <Stack direction="row" spacing={1} sx={{width:{xs:"100%",md:"auto"}}}>
                            <Button fullWidth variant="outlined" onClick={()=>setScheduleModal({open:true,schedule})}>Editar</Button>
                            <Button fullWidth variant="outlined" color="error" disabled={deleting===`schedule-${schedule.id}`} onClick={()=>deleteSchedule(schedule)}>{deleting===`schedule-${schedule.id}`?"Eliminando":"Eliminar"}</Button>
                          </Stack>
                        </Box>
                        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))",lg:"repeat(3,minmax(0,1fr))"},gap:1,marginTop:2}}>
                          {(schedule.ranges||[]).map((range)=>(
                            <Box key={range.id||`${range.weekday}-${range.entry_time}`} sx={{padding:1.5,borderRadius:2,backgroundColor:"#f9fafb",border:"1px solid #e5e7eb"}}>
                              <Typography sx={{fontSize:14,fontWeight:800}}>{range.weekday_name||WEEKDAYS[range.weekday]}</Typography>
                              <Typography sx={{color:"#4b5563",fontSize:13,fontWeight:700,marginTop:0.4}}>{formatTime(range.entry_time)} a {formatTime(range.exit_time)}</Typography>
                              {range.lunch_start&&range.lunch_end&&<Typography sx={{color:"#6b7280",fontSize:12,marginTop:0.3}}>Lunch: {formatTime(range.lunch_start)} a {formatTime(range.lunch_end)}</Typography>}
                              {!range.lunch_start&&Number(range.break_minutes||0)>0&&<Typography sx={{color:"#6b7280",fontSize:12,marginTop:0.3}}>Descanso: {range.break_minutes} minutos</Typography>}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
            {tab===1&&(
              <Box sx={{padding:{xs:2,sm:2.5}}}>
                {loading?<Box sx={{minHeight:240,display:"flex",alignItems:"center",justifyContent:"center"}}><CircularProgress /></Box>:assignments.length===0?<EmptyState title="No hay horarios asignados" text="Asigna un horario de asistencia a cada usuario." action="Asignar horario" onClick={()=>setAssignmentModal({open:true,assignment:null})} />:(
                  <Stack spacing={1.2}>
                    {assignments.map((assignment)=>(
                      <Box key={assignment.id} sx={{display:"flex",alignItems:{xs:"flex-start",md:"center"},justifyContent:"space-between",flexDirection:{xs:"column",md:"row"},gap:2,padding:2,border:"1px solid #e5e7eb",borderRadius:2,backgroundColor:"#ffffff"}}>
                        <Box>
                          <Typography sx={{fontSize:16,fontWeight:800}}>{assignment.person?.name}</Typography>
                          <Typography sx={{color:"#6b7280",fontSize:13}}>{assignment.person?.identification}</Typography>
                          <Typography sx={{fontSize:13,fontWeight:700,marginTop:0.8}}>Horario: {assignment.schedule?.name}</Typography>
                          <Typography sx={{color:"#6b7280",fontSize:12}}>Vigencia: {formatDate(assignment.valid_from)} hasta {formatDate(assignment.valid_until)}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} sx={{width:{xs:"100%",md:"auto"}}}>
                          <Button fullWidth variant="outlined" onClick={()=>setAssignmentModal({open:true,assignment})}>Editar</Button>
                          <Button fullWidth variant="outlined" color="error" disabled={deleting===`assignment-${assignment.id}`} onClick={()=>deleteAssignment(assignment)}>{deleting===`assignment-${assignment.id}`?"Eliminando":"Quitar"}</Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
            {tab===2&&(
              <Box sx={{padding:{xs:2,sm:2.5}}}>
                <Box sx={{display:"flex",alignItems:"center",gap:1.5,marginBottom:2,flexWrap:"wrap"}}>
                  <TextField type="date" size="small" value={recordDate} onChange={(e)=>setRecordDate(e.target.value)} sx={{width:{xs:"100%",sm:200}}} />
                  {recordDate!==getToday()&&<Button variant="outlined" onClick={()=>setRecordDate(getToday())}>Hoy</Button>}
                </Box>
                {loading?<Box sx={{minHeight:240,display:"flex",alignItems:"center",justifyContent:"center"}}><CircularProgress /></Box>:filteredRecords.length===0?<EmptyState title="No hay registros de asistencia" text={`No existen registros para el ${formatDate(recordDate)}.`} />:(
                  <Stack spacing={1.2}>
                    {filteredRecords.map((record)=>(
                      <Box key={record.id} sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"2fr repeat(6,minmax(100px,1fr))"},alignItems:"center",gap:1.5,padding:2,border:"1px solid #e5e7eb",borderRadius:2,backgroundColor:"#ffffff"}}>
                        <Box>
                          <Typography sx={{fontSize:15,fontWeight:800}}>{record.person?.name||"Sin usuario"}</Typography>
                          <Typography sx={{color:"#6b7280",fontSize:12}}>{record.person?.identification||"Sin identificación"}</Typography>
                        </Box>
                        <RecordValue label="Fecha" value={formatDate(record.attendance_date)} />
                        <RecordValue label="Entrada" value={formatTime(record.actual_entry)} />
                        <RecordValue label="Salida" value={formatTime(record.actual_exit)} />
                        <RecordValue label="Resultado" value={`${entryLabel(record.entry_result)} / ${exitLabel(record.exit_result)}`} />
                        <RecordValue label="Trabajado" value={formatMinutes(record.worked_minutes)} />
                        <Box>
                          <Typography sx={{color:"#6b7280",fontSize:12}}>Estado</Typography>
                          <Chip size="small" label={statusLabel(record.status)} sx={{marginTop:0.4,fontWeight:700}} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <AttendanceModals fetchUser={fetchUser} schedules={schedules} people={people} scheduleModal={scheduleModal} assignmentModal={assignmentModal} onCloseSchedule={()=>setScheduleModal({open:false,schedule:null})} onCloseAssignment={()=>setAssignmentModal({open:false,assignment:null})} onSaved={async(message)=>{showFeedback("success",message);await loadData();}} onError={(message)=>showFeedback("error",message)} />
      <Snackbar open={feedback.open} autoHideDuration={4000} onClose={()=>setFeedback((previous)=>({...previous,open:false}))} anchorOrigin={{vertical:"top",horizontal:"right"}}>
        <Alert severity={feedback.severity} variant="filled" onClose={()=>setFeedback((previous)=>({...previous,open:false}))}>{feedback.message}</Alert>
      </Snackbar>
    </Box>
  );
}

function EmptyState({title,text,action,onClick}){
  return (
    <Box sx={{minHeight:260,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center"}}>
      <Typography sx={{fontSize:20,fontWeight:800}}>{title}</Typography>
      <Typography sx={{color:"#6b7280",fontSize:14,marginTop:1,marginBottom:action?2:0}}>{text}</Typography>
      {action&&<Button variant="contained" onClick={onClick}>{action}</Button>}
    </Box>
  );
}

function RecordValue({label,value}){
  return (
    <Box>
      <Typography sx={{color:"#6b7280",fontSize:12}}>{label}</Typography>
      <Typography sx={{fontSize:13,fontWeight:700,marginTop:0.3}}>{value}</Typography>
    </Box>
  );
}